import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Stack, Typography } from '@mui/material';

import type { DashboardConfig } from 'src/types/dashboard';
import { formatDateString } from 'src/helper/helper';
import FilterDisplay from 'src/components/common/FilterDisplay';
import { useStatistics } from 'src/components/dashboard/DashboardContext/StatisticsContext';
import ButtonFollowUpDownloadModal from 'src/components/dashboard/DashboardPage/components/ButtonFollowUpDownloadModal';
import ButtonHelpModal from 'src/components/dashboard/DashboardPage/components/ButtonHelpModal';
import ButtonReportModal from 'src/components/dashboard/DashboardPage/components/ButtonReportModal';
import ConfidentialLogo from 'src/components/dashboard/DashboardPage/components/ConfidentialLogo';

export const DASHBOARD_HEADER_TEST_ID = 'dashboard-header-test-id';
export const DASHBOARD_HEADER_LAST_REFRESH_TEST_ID =
  'dashboard-header-last-refresh';

export interface DashboardHeaderProps {
  dashboardConfig: DashboardConfig;
  showFollowUpInspirations: boolean;
}

const DashboardHeader: FC<DashboardHeaderProps> = ({ dashboardConfig }) => {
  const { t, i18n } = useTranslation('dashboard');
  const {
    confidential,
    show_help: showHelp,
    show_download_reports: showDownloadReports,
    show_follow_up_download_button: showFollowUpDownloadButton,
  } = dashboardConfig;

  const { data } = useStatistics();

  return (
    <Box
      className="DashboardHeader-root"
      data-test-id={DASHBOARD_HEADER_TEST_ID}
      sx={{
        ml: 1,
        mb: 2,
      }}
    >
      <Grid container>
        <Grid
          item
          xs={12}
          md={8}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
          }}
        >
          <FilterDisplay
            formatLabel={(name, values) =>
              `${values.map((value) => value.label).join(', ')}`
            }
          />

          {data && data.last_data_refresh && (
            <Typography
              color="primary"
              data-test-id={DASHBOARD_HEADER_LAST_REFRESH_TEST_ID}
              gutterBottom
              sx={{
                pt: 1,
              }}
            >
              {t('LastUpdate', {
                date: formatDateString(data.last_data_refresh, i18n.language),
              })}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack
            direction="row"
            sx={{
              justifyContent: 'flex-end',
            }}
          >
            <Stack direction="column" spacing={2}>
              {confidential && <ConfidentialLogo />}
              {showHelp && <ButtonHelpModal />}
              {data?.focal_result.dataline && showDownloadReports && (
                <ButtonReportModal dataline={data.focal_result.dataline} />
              )}
              {showFollowUpDownloadButton && <ButtonFollowUpDownloadModal />}
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardHeader;
